import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { of, switchMap } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { Category } from '../entities/category.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { mongo } from '../server/mongo.js';
import { EinzelberatungDataview } from '../use-case-entities/EinzelberatungDataview.js';
import { lookupEntityById, withIdTiebreaker } from '../util.js';

export const subscribeEinzelberatungDataview = registerSubscription({
  operationName: 'subscribeEinzelberatungDataview',
  type: () => [EinzelberatungDataview],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<EinzelberatungDataview>) => {
      if (!filter.institutionID) return of([]);

      return watchQuery(Einzelberatung._collectionName, {
        _class: Einzelberatung._class,
        institutionID: filter.institutionID,
      }).pipe(
        switchMap(async () => {
          const collection = mongo().collection<Einzelberatung>('products-materialized');
          const { Participation } = await import('../entities/Participation.js');
          let pF = await publishFilter(Einzelberatung._class);
          if (context.serverContext) pF = {};
          if (!pF) throw new Error(`Missing publishFilter for ${Einzelberatung._class}`);

          const cFilter = {
            _class: Einzelberatung._class,
            institutionID: filter.institutionID,
            ...(filter.timeframeID ? { timeframeID: filter.timeframeID } : {}),
          };

          const aggregation = [
            { $match: { $and: [cFilter, pF] } },
            { $replaceRoot: { newRoot: { einzelberatung: '$$ROOT' } } },
            { $lookup: lookupEntityById(Participation, 'einzelberatung._id', 'participations') },
            { $lookup: lookupEntityById(Category, 'einzelberatung.categoryID', 'category') },
            { $set: { category: { $arrayElemAt: ['$category', 0] } } },
            {
              $addFields: {
                _class: EinzelberatungDataview._class,
                timeframeID: '$einzelberatung.timeframeID',
                institutionID: '$einzelberatung.institutionID',
              },
            },
            { $match: filter.getMongoFilter() },
            { $sort: withIdTiebreaker(filter.getMongoOptions().sort, 'einzelberatung._id') },
            { $project: gqlFields },
            { $skip: skip },
            { $limit: limit },
          ];

          return await collection.raw
            .aggregate<EinzelberatungDataview>(aggregation, { collation: { locale: 'de' } })
            .toArray();
        }),
      );
    };
  },
});
